import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Profile {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  date_of_birth: string | null;
  country_code: string | null;
  region_code: string | null;
  preferred_language: string | null;
  city: string | null;
  address_street: string | null;
  address_postal_code: string | null;
  home_lat: number | null;
  home_lng: number | null;
  use_home_as_recommendation_fallback: boolean;
  profile_code_rotated_at: string | null;
  profile_slug: string | null;
  profile_slug_updated_at: string | null;
  profile_slug_change_count: number;
  profile_moderation_status: 'clean' | 'needs_review' | 'blocked';
  contact_email: string | null;
  birthday_show_year: boolean;
  created_at: string;
  updated_at: string;
}

export interface DiverProfile {
  id: string;
  user_id: string;
  short_bio: string | null;
  experience_level_band: string | null;
  diving_since_year: number | null;
}

export interface PrivacySettings {
  id: string;
  user_id: string;
  show_real_name: boolean;
  show_certifications: boolean;
  show_location: boolean;
  show_phone: boolean;
  show_email: boolean;
  show_diver_profile: boolean;
  show_organisations: boolean;
  visibility_basic: 'public' | 'community' | 'buddy' | 'secret';
  visibility_dive_profile: 'public' | 'community' | 'buddy' | 'secret';
  visibility_organisations: 'public' | 'community' | 'buddy' | 'secret';
  visibility_stats: 'public' | 'community' | 'buddy' | 'secret';
  visibility_emergency_contacts: 'community' | 'buddy' | 'secret';
  visibility_phone: 'community' | 'buddy' | 'secret';
  visibility_email: 'community' | 'buddy' | 'secret';
  visibility_address: 'community' | 'buddy' | 'secret';
  visibility_birthday: 'public' | 'community' | 'buddy' | 'secret';
  visibility_favorites: 'public' | 'community' | 'buddy' | 'secret';
}

async function invokeIdentity(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke('identity-write', { body });
  if (error) {
    // FunctionsHttpError exposes the raw Response; surface JSON body so callers
    // can react to specific server errors (e.g. username_change_cooldown).
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.text === 'function') {
      try {
        const txt = await ctx.text();
        throw new Error(txt);
      } catch (e) {
        if (e instanceof Error && e.message) throw e;
      }
    }
    throw error;
  }
  if (data?.error) throw new Error(JSON.stringify(data));
  return data;
}

export function useProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile, isLoading, refetch } = useQuery({
    queryKey: ['profile', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user!.id)
        .single();
      if (error) throw error;
      return data as Profile;
    },
  });

  const updateProfile = useMutation({
    mutationFn: async (updates: Partial<Profile>) => invokeIdentity({ command: 'update_profile', updates }),
    onSuccess: (_data, updates) => {
      queryClient.setQueryData(['profile', user?.id], (current: Profile | undefined) =>
        current ? { ...current, ...updates } : current,
      );
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
    },
  });

  const updateProfileSlug = useMutation({
    mutationFn: async (newSlug: string) =>
      invokeIdentity({ command: 'update_profile_slug', new_slug: newSlug }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile', user?.id] }),
  });

  return { profile, isLoading, refetch, updateProfile, updateProfileSlug };
}

/**
 * Owner-only retrieval of the current user's raw buddy code.
 *
 * The raw code is stored in the `profile_code_secrets` table behind an
 * owner-scoped RLS policy and never returned by any other API/profile read.
 * Even admins cannot read it; they can only rotate it. The component that
 * displays/encodes the code (QR, fingerprint) calls this hook directly.
 */
export function useMyProfileCode() {
  const { user } = useAuth();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['my-profile-code', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_profile_code');
      if (error) throw error;
      return (data as string | null) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
  return { profileCode: data ?? null, isLoading, refetch };
}

export function useDiverProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: diverProfile, isLoading } = useQuery({
    queryKey: ['diver-profile', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('diver_profiles')
        .select('id, user_id, short_bio, experience_level_band, diving_since_year')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return data as DiverProfile;
    },
  });

  const updateDiverProfile = useMutation({
    mutationFn: async (updates: Partial<DiverProfile>) => invokeIdentity({ command: 'update_diver_profile', updates }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['diver-profile', user?.id] }),
  });

  return { diverProfile, isLoading, updateDiverProfile };
}

export function usePrivacySettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: privacy, isLoading } = useQuery({
    queryKey: ['privacy-settings', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('privacy_settings')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as PrivacySettings | null;
    },
  });

  const updatePrivacy = useMutation({
    mutationFn: async (updates: Partial<PrivacySettings>) => invokeIdentity({ command: 'update_privacy_settings', updates }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['privacy-settings', user?.id] }),
  });

  return { privacy, isLoading, updatePrivacy };
}
